import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { BUILDER } from "@/constants/testIds";

const NODE_W = 148;
const NODE_H = 172;

export default function TreeCanvas({
  people, rels, layout, selectedId, onSelect,
}) {
  const { positions, width, height } = layout;

  const paths = useMemo(() => {
    const out = [];
    rels.forEach((r) => {
      const A = positions[r.from_id];
      const B = positions[r.to_id];
      if (!A || !B) return;
      if (r.type === "parent") {
        // curve from A (parent) bottom -> B (child) top
        const ax = A.x, ay = A.y + NODE_H / 2;
        const bx = B.x, by = B.y - NODE_H / 2;
        const midY = ay + (by - ay) / 2;
        out.push({
          id: r.id, kind: "parent",
          d: `M ${ax} ${ay} C ${ax} ${midY}, ${bx} ${midY}, ${bx} ${by}`,
        });
      } else if (r.type === "spouse") {
        const y = A.y;
        // simple horizontal double-line
        const ax = A.x + NODE_W / 2, bx = B.x - NODE_W / 2;
        if (ax < bx) {
          out.push({ id: r.id, kind: "spouse", d: `M ${ax} ${y} L ${bx} ${y}` });
        } else {
          out.push({ id: r.id, kind: "spouse", d: `M ${bx} ${y} L ${ax} ${y}` });
        }
      }
    });
    return out;
  }, [rels, positions]);

  return (
    <div
      data-testid={BUILDER.canvas}
      className="relative w-full overflow-auto bg-[radial-gradient(circle_at_2px_2px,rgba(45,74,62,0.14)_1px,transparent_0)] [background-size:22px_22px]"
      style={{ maxHeight: 640 }}
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="block"
      >
        {paths.map((p) => (
          <motion.path
            key={p.id}
            d={p.d}
            fill="none"
            stroke={p.kind === "spouse" ? "#E86A33" : "#2D4A3E"}
            strokeOpacity={p.kind === "spouse" ? 0.85 : 0.55}
            strokeWidth={p.kind === "spouse" ? 1.5 : 1.4}
            strokeDasharray={p.kind === "spouse" ? "4 5" : "0"}
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          />
        ))}
      </svg>
      {people.map((p) => {
        const pos = positions[p.id];
        if (!pos) return null;
        const active = selectedId === p.id;
        return (
          <PersonNode
            key={p.id}
            person={p}
            x={pos.x}
            y={pos.y}
            active={active}
            onClick={() => onSelect(p.id)}
          />
        );
      })}
    </div>
  );
}

function PersonNode({ person, x, y, active, onClick }) {
  const initials = person.name.trim().charAt(0).toUpperCase();
  return (
    <motion.button
      data-testid={BUILDER.personNode(person.id)}
      onClick={onClick}
      initial={{ opacity: 0, scale: 0.7 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.96 }}
      className={`absolute -translate-x-1/2 -translate-y-1/2 group text-left z-10 ${active ? "z-20" : ""}`}
      style={{ left: x, top: y, width: NODE_W }}
    >
      <div className={`relative rounded-[999px_999px_16px_16px] overflow-hidden border transition ${
        active
          ? "border-saffron shadow-[0_16px_50px_-20px_rgba(232,106,51,0.55)]"
          : "border-ivory-300 shadow-[0_10px_30px_-24px_rgba(10,15,13,0.35)]"
      }`}>
        {person.portrait_url ? (
          <img
            src={person.portrait_url}
            alt={person.name}
            className="w-full h-[124px] object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div
            className={`w-full h-[124px] flex items-center justify-center ${
              person.gender === "female" ? "bg-[#F6D6C7]"
              : person.gender === "male" ? "bg-[#CBD8D0]"
              : "bg-ivory-200"
            }`}
          >
            <span className="font-serif text-4xl text-tulsi">{initials}</span>
          </div>
        )}
        {person.is_you && (
          <span className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.18em] bg-saffron text-ivory px-2 py-0.5 rounded-full">
            You
          </span>
        )}
      </div>
      <div className="mt-2 text-center px-1">
        <div className="font-serif text-[14px] text-ink leading-tight truncate">{person.name}</div>
        <div className="text-[10px] uppercase tracking-[0.16em] text-ink-50 mt-0.5">
          {[person.birth_year, person.death_year].filter(Boolean).join(" — ") || " "}
        </div>
      </div>
    </motion.button>
  );
}
